import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  UpdateImpactMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export interface UpdateImpactTRPCInput {
  Id: string;
  Name: string;
  Rationale?: InputMaybe<string>;
  RatingGuidance?: InputMaybe<string>;
  LikelihoodAppetite?: InputMaybe<number>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateImpactMutation => {
  return {
    updateImpactApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateImpactTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.impact.update.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impacts.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactList.queryKey(),
          }),
          // Impact updates may affect rating views
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.impactRatingsWithAppetites.queryKey(),
          }),
        ]);
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    updateImpact: async (
      variables: UpdateImpactTRPCInput
    ): Promise<UpdateImpactMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
